// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { Action } from "redux"
import {
  CreateFavouriteActivityTypeRequest,
  FavouriteActivityType,
  ListRecentActivityTypesRequest
} from "generated/legebill/site/activitytype/v4/activity_type_pb"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { grpc } from "app/rpc/executor"
import { activityTypeServiceClient } from "app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { RootState } from "../../../app/store"
import { SiteId } from "../../user-context/slice"
import { RECENT_LIST_ACTIVITY_TYPES_COUNT } from "../../shared/search-dropdown/config"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type CreateFavouriteActivityTypeRpc = (request: CreateFavouriteActivityTypeRequest) => Observable<FavouriteActivityType>

export const createFavouriteActivityType = (createFavouriteActivityTypeRpc: CreateFavouriteActivityTypeRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createFavouriteActivityType.match),
      mergeMap(({ payload }) => of(payload)
        .pipe(
          mergeMap(createFavouriteActivityTypeRpc),
          map(t => t.toObject()),
          map(Slice.createFavouriteActivityTypeFulfilled),
          catchError(e => of(e)
            .pipe(
              withLatestFrom(state$),
              mergeMap(([, state]) => handleError(e, state.userContext.currentSiteId))
            )
          ),
        )
      ),
    )

const createFavouriteActivityTypeRpc = (request: CreateFavouriteActivityTypeRequest): Observable<FavouriteActivityType> =>
  grpc(() => activityTypeServiceClient.createFavouriteActivityType(request, null))

const handleError = (e: unknown, siteId: SiteId | undefined) => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND && siteId !== undefined) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_favourite_activity_type_not_found", message: "Activity type not found." }),
          messageType: "ERROR",
        })),
        of(Actions.fetchRecentActivityTypes({
          request: new ListRecentActivityTypesRequest()
            .setSiteId(siteId)
            .setPageSize(RECENT_LIST_ACTIVITY_TYPES_COUNT)
        }))
      )
    }

    if (code === StatusCode.PERMISSION_DENIED && siteId !== undefined) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_favourite_activity_type_permission_denied",
            message: "Failed to favourite activity type. You do not have permission to access this activity type."
          }),
          messageType: "ERROR",
        })),
        of(Actions.fetchRecentActivityTypes({
          request: new ListRecentActivityTypesRequest()
            .setSiteId(siteId)
            .setPageSize(RECENT_LIST_ACTIVITY_TYPES_COUNT)
        }))
      )
    }
  }

  return of(showSnackBar({
    message: t({
      id: "msg_favourite_activity_type_failed",
      message: "Failed to favourite activity type. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
    }),
    messageType: "ERROR",
  }))
}

export default createFavouriteActivityType(createFavouriteActivityTypeRpc)
